/** @format */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { configDefault } from '../../core/config';
import { useDispatch } from 'react-redux';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';
import PaperedTable from '../../components/Table/PaperedTable';
import { TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import TableHeaderContentCell from '../../components/Table/TableHeaderContentCell';
import EditButton from '../../components/Buttons/EditButton';
import I18n from '../../components/Translate/I18n';
import Pager from '../../components/Adescom/Pager';
import { tariffConstants } from '../../modules/tariffs/actions';
import { makeStyles } from '@material-ui/core/styles';
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import { DateHelperTs } from '../../helpers/DateHelperTs';
import Loader from '../../components/Loaders/Loader';
import CancelButton from '../../components/Buttons/CancelButton';
import TariffInterface from '../../interfaces/billing/TariffInterface';
import Paper from '@material-ui/core/Paper';
import Card from '../../components/Card/Card';
import { LinearBillingEdit } from './LinearBillingEdit';
import { LinearBillingInterface } from '../../interfaces/billing/LinearBillingInterface';
import PricelistHistoryInterface, { PricelistHistoryCollectionInterface } from '../../interfaces/billing/PricelistHistoryInterface';

const useStyle = makeStyles(adescomStyle);

interface LinearBillingHistoryListProps {
    tariff: TariffInterface;
    linearBilling: LinearBillingInterface;
    historyList: PricelistHistoryCollectionInterface | undefined;
    updateHistoryList: (historyList: PricelistHistoryCollectionInterface) => void;
}

export const LinearBillingHistoryList = (props: LinearBillingHistoryListProps): React.ReactElement => {
    const classes = useStyle();

    const historyList = props.historyList;
    const updateHistoryList = props.updateHistoryList;

    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    const linearBilling = props.linearBilling;

    const [loading, setLoading] = useState<boolean>(false);
    const [linearBillingDialogOpen, setLinearBillingDialogOpen] = useState<boolean>(false);
    const [selectedPricelist, setSelectedPricelist] = useState<PricelistHistoryInterface | null>(null);

    const tariff = useMemo((): TariffInterface => {
        return props.tariff;
    }, [props.tariff]);

    const dispatch = useDispatch();

    const [defaultFilters, setDefaultFilters] = useState<any>([
        { name: 'tariffId', value: tariff.id },
        { name: 'mainname', value: encodeURIComponent(props.linearBilling.mainname!) },
    ]);
    const [defaultOrders, setDefaultOrders] = useState<any>([{ name: 'latestUpdatedAt', value: 'DESC' }]);
    const [filters, setFilters] = useState<any>([]);
    const [orders, setOrders] = useState<any>([]);

    const getPricelists = useCallback((): void => {
        const tmpFilters = [...defaultFilters, ...filters];
        const tmpOrders = [...defaultOrders, ...orders];

        setLoading(true);
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/pricelist_histories', configDefault.PLATFORM_API_PREFIX)),
            'PRICELIST_LIST',
            tmpFilters,
            tmpOrders,
            {
                onSuccess: (data: PricelistHistoryCollectionInterface) => {
                    setLoading(false);
                    updateHistoryList(data);
                },
                onError: () => {
                    setLoading(false);
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [dispatch, defaultFilters, defaultOrders, filters, orders, updateHistoryList]);

    useEffect(() => {
        if (historyList || !linearBilling || linearBilling.mainname === '') {
            return;
        }
        const tmpOrders = orders;
        const tmpFilters = filters;
        getPricelists();
    }, [getPricelists, orders, filters, historyList, linearBilling]);

    const addLinearBilling = useCallback((param: any): void => {
        setSelectedPricelist(null);
        setLinearBillingDialogOpen(true);
    }, []);

    const editLinearBilling = useCallback((param: any): void => {
        setLinearBillingDialogOpen(true);
        setSelectedPricelist(param.pricelist);
    }, []);

    const paginationCallbackFunction = useCallback(
        (uri: string): void => {
            const tmpFilters = [...defaultFilters, ...filters];
            const tmpOrders = [...orders];

            setLoading(true);
            const itemLoaders = new ItemLoaders(dispatch);
            itemLoaders.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                tariffConstants.LIST,
                tmpFilters,
                tmpOrders,
                {
                    onSuccess: (data: PricelistHistoryCollectionInterface) => {
                        setLoading(false);
                        updateHistoryList(data);
                    },
                    onError: () => {
                        setLoading(false);
                    },
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch, filters, defaultFilters, orders, updateHistoryList]
    );

    const closeLinearBillingDialog = useCallback(
        (reloadData = false): void => {
            setLinearBillingDialogOpen(false);
            if (reloadData) {
                if (!historyList) {
                    getPricelists();
                } else {
                    paginationCallbackFunction(historyList['hydra:view']['@id']);
                }
            }
        },
        [paginationCallbackFunction, historyList, getPricelists]
    );

    const updateFilters = useCallback(
        (newFilters: any): void => {
            setFilters(newFilters);
        },
        [setFilters]
    );

    const updateOrders = useCallback(
        (newOrders: any): void => {
            setOrders(newOrders);
        },
        [setOrders]
    );

    const typeSelect: any = useMemo((): any => {
        return [
            { name: 'Połączenie głosowe', value: 0 },
            { name: 'Usługa', value: 1 },
        ];
    }, []);

    return (
        <>
            <Paper>
                <Card>
                    <GridContainer>
                        <GridItem xs={12}>
                            <h3>
                                <I18n trans={'pricelists:lb.pricelists_history'} />
                            </h3>
                        </GridItem>
                        <GridItem xs={12}>
                            <PaperedTable>
                                <TableHead>
                                    <TableRow>
                                        <TableCell classes={tableStickyCommand} padding="none" align="left">
                                            <TableHeaderContentCell textAlign="left" title="general:common.commands" onlyFilter={true} />
                                            {(orders.length > 0 || filters.length > 0) && (
                                                <CancelButton
                                                    callback={(): void => {
                                                        setFilters([]);
                                                        setOrders([]);
                                                    }}
                                                    callbackParam={{}}
                                                    tooltipTitle="general:common.clear_filter"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="left">
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="pricelists:lb.activedate"
                                                updateOrderList={updateOrders}
                                                updateFilterList={updateFilters}
                                                fieldName="activeDate"
                                                showSort={false}
                                                defaultDirection="desc"
                                                searchFieldType={null}
                                                dateFormat="DD-MM-RRRR"
                                                beforeSetToMax={false}
                                                afterSetToMin={false}
                                                dateFromTo={false}
                                                breakLine={false}
                                                timeFormat={false}
                                                filters={filters}
                                                sort={orders}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="left">
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="pricelists:lb.deactivationDate"
                                                updateOrderList={updateOrders}
                                                updateFilterList={updateFilters}
                                                fieldName="deactivationDate"
                                                showSort={false}
                                                defaultDirection="desc"
                                                searchFieldType={null}
                                                dateFormat="DD-MM-RRRR"
                                                beforeSetToMax={false}
                                                afterSetToMin={false}
                                                dateFromTo={false}
                                                breakLine={false}
                                                timeFormat={false}
                                                filters={filters}
                                                sort={orders}
                                            />
                                        </TableCell>
                                        <TableCell classes={tableStickyHeader} align="left">
                                            <TableHeaderContentCell
                                                textAlign="left"
                                                title="pricelists:lb.updatedAt"
                                                updateOrderList={updateOrders}
                                                updateFilterList={updateFilters}
                                                fieldName="updatedAt"
                                                showSort={false}
                                                defaultDirection="desc"
                                                searchFieldType={null}
                                                dateFormat="DD-MM-RRRR"
                                                beforeSetToMax={false}
                                                afterSetToMin={false}
                                                dateFromTo={false}
                                                breakLine={false}
                                                timeFormat={false}
                                                filters={filters}
                                                sort={orders}
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <Loader visible={loading} />
                                    {historyList &&
                                        historyList['hydra:member'] &&
                                        historyList['hydra:member'].map((linearBilling: PricelistHistoryInterface) => {
                                            return (
                                                <TableRow key={linearBilling.customId}>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_CUSTOM_BILLING_ACTION_HISTORICAL_EDIT) && (
                                                            <EditButton
                                                                tooltipTitle="pricelists:b.edit_pricelist_element"
                                                                callback={editLinearBilling}
                                                                callbackParam={{ pricelist: linearBilling }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {linearBilling.activeDate && DateHelperTs.format(linearBilling.activeDate, 'DD-MM-YYYY')}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {linearBilling.deactivationDate &&
                                                            DateHelperTs.format(linearBilling.deactivationDate, 'DD-MM-YYYY')}
                                                    </TableCell>
                                                    <TableCell classes={tableCellRoot} align="left">
                                                        {linearBilling.latestUpdatedAt &&
                                                            DateHelperTs.format(linearBilling.latestUpdatedAt, 'DD-MM-YYYY')}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                                <TableFooter>
                                    <TableRow>
                                        <TableCell classes={tableCellRoot} colSpan={5}>
                                            {historyList && historyList['hydra:view'] && (
                                                <Pager callback={paginationCallbackFunction} views={historyList['hydra:view']} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                </TableFooter>
                            </PaperedTable>
                        </GridItem>
                    </GridContainer>
                </Card>
            </Paper>
            {linearBillingDialogOpen && (
                <LinearBillingEdit
                    tariff={tariff}
                    selectedPricelist={selectedPricelist}
                    open={linearBillingDialogOpen}
                    closeDialog={closeLinearBillingDialog}
                />
            )}
        </>
    );
};
